import { useQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import Spinner from '../UIFeedback/Spinner/Spinner';
import { GET_NOTIFICATION_PREFERENCES } from '../../services/apollo/queries/userQueries';
import { UPDATE_NOTIFICATION_PREFERENCES } from '../../services/apollo/mutations/userMutations';

const PREFERENCE_KEYS = [
  'postExpiringWarning',
  'postExpiredNotice',
  'marketing',
];

const PREFERENCE_COPY = {
  postExpiringWarning: {
    labelKey: 'pref_post_expiring_warning_label',
    labelDefault: 'Post expiring reminders',
    descriptionKey: 'pref_post_expiring_warning_description',
    descriptionDefault:
      'Get an email 7 days and 1 day before one of your posts expires.',
  },
  postExpiredNotice: {
    labelKey: 'pref_post_expired_notice_label',
    labelDefault: 'Post expired notices',
    descriptionKey: 'pref_post_expired_notice_description',
    descriptionDefault:
      "Let us know when one of your posts has expired so you don't miss it.",
  },
  marketing: {
    labelKey: 'pref_marketing_label',
    labelDefault: 'Updates and announcements',
    descriptionKey: 'pref_marketing_description',
    descriptionDefault:
      'Occasional product news, tips, and community updates from All Animal App.',
  },
};

const ToggleSwitch = ({ checked, disabled, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={onChange}
    disabled={disabled}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed ${
      checked ? 'bg-primary' : 'bg-gray-300'
    }`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
        checked ? 'translate-x-5' : 'translate-x-0.5'
      }`}
    />
  </button>
);

const NotificationPreferences = () => {
  const { t } = useTranslation();

  const { data, loading, error } = useQuery(GET_NOTIFICATION_PREFERENCES, {
    fetchPolicy: 'cache-and-network',
  });

  const [updatePreferences] = useMutation(UPDATE_NOTIFICATION_PREFERENCES);

  const user = data?.user;
  const prefs = user?.notificationPreferences;

  const handleToggle = (key) => {
    if (!user || !prefs) return;
    const newValue = !prefs[key];

    updatePreferences({
      variables: { input: { [key]: newValue } },
      optimisticResponse: {
        updateNotificationPreferences: {
          __typename: 'User',
          _id: user._id,
          notificationPreferences: {
            __typename: 'NotificationPreferences',
            ...prefs,
            [key]: newValue,
          },
        },
      },
    }).catch((err) => {
      console.error('Failed to update notification preferences:', err);
    });
  };

  if (loading && !prefs) {
    return (
      <div className="flex items-center justify-center py-10">
        <Spinner />
      </div>
    );
  }

  if (error && !prefs) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-10 px-4">
        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-3">
          <AlertCircle className="w-6 h-6 text-red-500" />
        </div>
        <p className="text-sm text-muted-foreground max-w-xs">
          {t(
            'pref_load_error',
            "We couldn't load your notification preferences. Please try again."
          )}
        </p>
      </div>
    );
  }

  if (!prefs) return null;

  return (
    <div className="space-y-3">
      <p className="text-xs sm:text-sm text-muted-foreground">
        {t(
          'email_notifications_subtitle',
          'Choose which emails you want to receive from All Animal App.'
        )}
      </p>

      <ul className="bg-white rounded-lg border border-border divide-y divide-border overflow-hidden">
        {PREFERENCE_KEYS.map((key) => {
          const copy = PREFERENCE_COPY[key];
          const checked = !!prefs[key];
          const label = t(copy.labelKey, copy.labelDefault);
          return (
            <li
              key={key}
              className="flex items-start justify-between gap-3 p-3 sm:p-4"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground">{label}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t(copy.descriptionKey, copy.descriptionDefault)}
                </p>
              </div>
              <div className="pt-1 flex-shrink-0">
                <ToggleSwitch
                  checked={checked}
                  onChange={() => handleToggle(key)}
                  label={label}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default NotificationPreferences;
